import {
  useSidebarContext,
  WORKSPACE_RAIL_COLLAPSED_NAV_WIDTH,
  type WorkspaceRail,
} from "@/context/SidebarContext";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { WorkspaceHeaderButton } from "@/layouts/WorkspaceLayout/components/WorkspaceHeaderButton";
import { useWorkspaceNavZoneWidth } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/WorkspaceLeftRail";
import type { Flags } from "@/providers/LaunchDarklyProvider";
import LogoIcon from "@/svg/brand/cohesive-logo-icon.svg?react";
import { cn } from "@/utils";
import { trackRailSelected, trackRailToggled } from "@/utils/analytics";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  Blocks,
  ChevronsLeft,
  Layers,
  MessagesSquare,
  ScrollText,
  Zap,
} from "lucide-react";
import React from "react";

// The consolidated rail replaced the separate Files / Chats rails, so three
// rails are always present: Workspaces (which nests canvases/chats/files),
// Skills, and Connectors. The Canvas / Chat explorer tabs are contextual —
// appended only while a canvas / workspace chat is open.
const BASE_RAIL_NAVS: {
  key: WorkspaceRail;
  icon: typeof Layers;
  label: string;
}[] = [
  { key: "workspaces", icon: Layers, label: "Workspaces" },
  { key: "chats", icon: MessagesSquare, label: "Chats" },
  { key: "skills", icon: ScrollText, label: "Skills" },
  { key: "connectors", icon: Blocks, label: "Connectors" },
  { key: "automations", icon: Zap, label: "Automations" },
];

interface WorkspaceAppHeaderProps {
  tabBar: React.ReactNode;
  utilities: React.ReactNode;
  showTabBar?: boolean;
}

export function WorkspaceAppHeader({
  tabBar,
  utilities,
  showTabBar = true,
}: WorkspaceAppHeaderProps) {
  const lg = useBreakpoint("lg");
  const navZoneWidth = useWorkspaceNavZoneWidth();
  const flags = useFlags<Flags>();
  // The active container's contents live in the rail DRAWER now, so the nav
  // strip is the persistent views — no contextual tabs. Automations is the
  // one flag-gated view (LD `automations`, per-org rollout).
  const railNavs = BASE_RAIL_NAVS.filter(
    (n) => n.key !== "automations" || flags.automations,
  );
  const {
    railOpen,
    isDrawerOpen,
    activeRail,
    toggleRail,
    selectRail,
    railResizing,
    navZoneRef,
    railWidthRef,
  } = useSidebarContext();

  const isRailVisible = lg ? railOpen : isDrawerOpen;
  const showRailNavIcons = lg ? isRailVisible : true;

  const handleSelectRail = (rail: WorkspaceRail) => {
    trackRailSelected({ rail, source: "header" });
    selectRail(rail);
  };

  const handleToggleRail = () => {
    trackRailToggled({ open: !isRailVisible, source: "header" });
    toggleRail();
  };

  return (
    <header className="relative z-20 flex h-11 shrink-0 items-center bg-surface-tab-bar">
      {/* Nav — fixed width tracks the pane edge. During drag the width is
          written to the DOM synchronously (navZoneRef / railWidthRef, the
          same values the pane's resize reports), so the seam above the pane
          never lags the handle. While the rail is open its bottom border
          goes transparent — the seam reads on the pane's own edge. */}
      <div
        ref={navZoneRef}
        className={cn(
          "flex h-full shrink-0 items-center gap-0.5 overflow-hidden border-b px-2",
          !railResizing &&
            "transition-[width,border-color] duration-300 ease-out",
          isRailVisible ? "border-transparent" : "border-n-10",
        )}
        style={{
          width: railResizing ? railWidthRef.current : navZoneWidth,
        }}
      >
        <div className="flex min-w-0 flex-1 items-center gap-0.5 overflow-hidden">
          {/* The view switchers live at the header level (main's docked
              arrangement) — the pane below starts at its search row. */}
          {showRailNavIcons &&
            railNavs.map((n) => (
              <WorkspaceHeaderButton
                key={n.key}
                icon={n.icon}
                label={n.label}
                pressed={activeRail === n.key}
                onClick={() => handleSelectRail(n.key)}
              />
            ))}
        </div>
        {lg ? (
          isRailVisible ? (
            <WorkspaceHeaderButton
              icon={ChevronsLeft}
              label="Collapse panel"
              onClick={handleToggleRail}
            />
          ) : (
            <button
              type="button"
              onClick={handleToggleRail}
              aria-label="Expand panel"
              title="Expand panel"
              className="inline-flex size-8 shrink-0 items-center justify-center text-n-50 outline-none transition-colors hover:text-n-90 focus-visible:rounded-md focus-visible:outline-2 focus-visible:outline-rust focus-visible:outline-offset-[-2px]"
            >
              <LogoIcon className="size-5" />
            </button>
          )
        ) : isDrawerOpen ? (
          <WorkspaceHeaderButton
            icon={ChevronsLeft}
            label="Close panel"
            onClick={handleToggleRail}
          />
        ) : null}
      </div>

      {/* Tabs — fluid, main column only */}
      {showTabBar ? (
        <nav
          aria-label="Open boards"
          className="flex h-full min-w-0 flex-1 items-center gap-0.5 overflow-hidden border-b border-n-10 pl-0.5 pr-2"
        >
          {tabBar}
        </nav>
      ) : (
        <div className="min-w-0 flex-1 border-b border-n-10" />
      )}

      {/* Utilities */}
      <div className="flex h-full shrink-0 items-center gap-0.5 border-b border-n-10 px-2">
        {utilities}
      </div>
    </header>
  );
}

export { WORKSPACE_RAIL_COLLAPSED_NAV_WIDTH };
